import { db } from '@/src/renderer/lib/db/indexeddb'
import { clearUserDisplayProfileCache } from '@/src/renderer/hooks/useUserDisplayProfile'
import { clearNavigationHistory } from '@/src/renderer/hooks/multiWindow/useNavigationHistory'
import { getPlatformAPI } from '@/src/renderer/lib/platform'

/**
 * Keys in localStorage that are scoped to the currently logged-in user and
 * must be removed on logout / user-switch. App-wide preferences (theme,
 * language, displayMode) are deliberately NOT listed — they survive.
 *
 * NOTE: `hasLoggedInBefore` is deliberately NOT in this list — it is a
 * device-level hint that a registered user has used this device, and it
 * must survive logout so the setup page defaults to the login view
 * instead of the register view on next visit.
 */
const USER_SCOPED_KEYS = [
  'loginMethod',
  'profileSetupCompleted',
  'onboarding_completed',
  'offlineSetupCompleted',
  'passwordRecoveryMode',
  'passwordRecoveryHash',
  'customLifeSpaces',
  'spaceOrder',
  'activeLifeSpace',
  'sidebar-state',
  'lastUserId',
]

/**
 * Prefix-matched localStorage keys (e.g. `tab_session_personal`, `quicknotes_work`)
 * that are user-scoped. All keys starting with any of these prefixes are removed.
 */
const USER_SCOPED_PREFIXES = ['tab_session_', 'quicknotes_', 'setting_']

/**
 * Dexie tables that hold a shared read-only catalog (same content for every
 * user) and are therefore preserved across the logout wipe. Re-fetching them
 * is wasteful (image blobs ~250 KB each from Bunny CDN) and there is no
 * privacy risk — they contain no user-specific data. The renderer's catalog
 * sync reconciles them against the server version on next access.
 */
const PRESERVED_TABLES = new Set(['avatars', 'avatarCategories'])

/**
 * Wipe all user-scoped local state. Called from:
 *  - AuthProvider.handleLogout after a successful supabase.auth.signOut()
 *  - AuthProvider's onAuthStateChange listener when detecting a user-id mismatch
 *    against the previously stored lastUserId (safety net for sessions that
 *    were not logged out cleanly).
 *
 * Preserves app-wide preferences (theme, language, displayMode) and the
 * shared avatar catalog (see PRESERVED_TABLES). The Supabase session key
 * (sb-*-auth-token) is handled by signOut() itself — we do not touch it here.
 */
export async function wipeUserLocalState(): Promise<void> {
  // 1. Wipe Dexie selectively — clear every table except the shared
  // read-only catalogs. Iterating `db.tables` keeps this in sync as new
  // tables are added (new tables are wiped by default, the safer failure
  // mode than forgetting to add them to a denylist).
  try {
    await Promise.all(
      db.tables
        .filter((table) => !PRESERVED_TABLES.has(table.name))
        .map((table) => table.clear())
    )
  } catch (err) {
    console.error('[wipeUserLocalState] Dexie wipe failed:', err)
  }

  // 2. sessionStorage is uniformly user-scoped in this app — clear it wholesale.
  try {
    sessionStorage.clear()
  } catch (err) {
    console.warn('[wipeUserLocalState] sessionStorage.clear failed:', err)
  }

  // 3. Remove explicit user-scoped localStorage keys.
  for (const key of USER_SCOPED_KEYS) {
    localStorage.removeItem(key)
  }

  // 4. Remove prefix-keyed localStorage entries.
  const keysToDelete: string[] = []
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i)
    if (!key) continue
    if (USER_SCOPED_PREFIXES.some((prefix) => key.startsWith(prefix))) {
      keysToDelete.push(key)
    }
  }
  for (const key of keysToDelete) {
    localStorage.removeItem(key)
  }

  // 5. Drop the in-memory user-display-profile cache so the next user (or the
  // logged-out state) never briefly shows the previous user's name/avatar.
  clearUserDisplayProfileCache()
  // Der Seitenverlauf (#225): Speicher und der Verlauf der offenen App — beim Kontowechsel ohne
  // Abmelden bleibt das Hauptfenster stehen und hätte die Seiten des vorigen Kontos zurückgeschrieben.
  clearNavigationHistory()

  // 6. Die Erinnerungen des alten Kontos: Ihr Plan liegt außerhalb von Dexie (Main-Prozess bzw.
  // `reminders.state`), und nach dem Abmelden schickt kein Fenster mehr einen neuen.
  try {
    await getPlatformAPI().reminders?.clear()
  } catch (err) {
    console.warn('[wipeUserLocalState] reminders clear failed:', err)
  }
}
